// The one app-level undo toast. Mount it ONCE (App.tsx); open it with showUndo()
// from src/lib/undoToast.ts. It is not save feedback — <SubmitOverlay/> still
// owns pending / tick / error; this is what is left when a row has just left the
// list and the operator may want it back.
//
// Visible UNDO_TOAST_MS; the timer pauses while the pointer is over it or focus
// is inside it. A separate, always-mounted role="status" (polite) announces it. It sits on the chrome rung (under a modal) above the
// bottom nav and the safe-area inset, and never uses a transform on an ancestor (see src/lib/motion.ts)
// — the fade-in is a `motion-safe:` keyframe on the card itself, no slide.
// The card is a dark island (data-theme="dark"), like the other app overlays.

import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { X } from 'lucide-react'
import { dismissUndo, getUndoToast, subscribeUndoToast, UNDO_TOAST_MS } from '@/lib/undoToast'

export default function UndoToast() {
  const toast = useSyncExternalStore(subscribeUndoToast, getUndoToast, getUndoToast)
  const [paused, setPaused] = useState(false)
  const id = toast?.id
  const remaining = useRef(UNDO_TOAST_MS)

  // A new toast starts a fresh clock.
  useEffect(() => { remaining.current = UNDO_TOAST_MS; setPaused(false) }, [id])

  useEffect(() => {
    if (id === undefined || paused) return
    const started = Date.now()
    const t = setTimeout(dismissUndo, remaining.current)
    return () => {
      clearTimeout(t)
      remaining.current = Math.max(0, remaining.current - (Date.now() - started))
    }
  }, [id, paused])

  // The live region stays mounted and only its text changes: a screen reader
  // does not announce a role="status" that arrives already filled.
  const announcer = <div role="status" aria-live="polite" className="sr-only">{toast?.message ?? ''}</div>

  if (!toast) return announcer

  const onUndo = () => {
    const fn = toast.onUndo
    dismissUndo()
    void fn()
  }

  return (
    <>
    {announcer}
    <div
      className="fixed inset-x-0 bottom-4 above-bottom-nav-gap z-chrome flex justify-center px-4 pointer-events-none print:hidden"
    >
      <div
        key={toast.id}
        data-theme="dark"
        data-testid="undo-toast"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setPaused(false) }}
        className="pointer-events-auto flex items-center gap-1 max-w-md w-full rounded-xl bg-raised text-fg border border-line-strong shadow-lg pl-4 pr-1 motion-safe:animate-[dialog-in_150ms_ease-out]"
      >
        <span className="flex-1 py-3 text-sm">{toast.message}</span>
        <button
          type="button"
          onClick={onUndo}
          className="min-h-[44px] min-w-[44px] px-3 text-sm font-semibold text-success hover:underline rounded-lg"
        >
          Undo
        </button>
        <button
          type="button"
          onClick={dismissUndo}
          aria-label="Dismiss"
          className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-fg-secondary hover:text-fg rounded-lg"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
    </>
  )
}
